/** Consent-gated browser analytics. Callers supply sanitized platform locations. */
export const CONSENT_KEY = "ando.analytics-consent.v1";
export const CONSENT_EVENT = "ando:analytics-consent";
export const SETTINGS_EVENT = "ando:cookie-settings";
const MAX_AGE = 180 * 24 * 60 * 60 * 1000;
const PUBLIC_PATH =
  /^\/(?:|engine|companybrain|presentation|download|privacy|terms|cookies|landing|products\/workspace|settori\/(?:distribuzione|manifatturiero|studi-aec|finanza-crediti))\/?$/;
export function publicLocation(value: string): string | null {
  try {
    const url = new URL(
      value,
      typeof window === "undefined" ? "https://ando-ai.com" : window.location.origin,
    );
    return PUBLIC_PATH.test(url.pathname) ? `${url.origin}${url.pathname}` : null;
  } catch {
    return null;
  }
}
const failedStorageKeys = new Set<string>();
export function readConsent(key = CONSENT_KEY): boolean | null {
  if (failedStorageKeys.has(key)) return false;
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null");
    return saved &&
      typeof saved.accepted === "boolean" &&
      Number.isFinite(saved.at) &&
      saved.at <= Date.now() &&
      Date.now() - saved.at < MAX_AGE
      ? saved.accepted
      : null;
  } catch {
    return null;
  }
}
type AnalyticsWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
let activeId = "";
let lastPage = "";
let activeConsentKey = CONSENT_KEY;
let activeSurface: "web" | "platform" = "web";
function command(...args: unknown[]) {
  const w = window as AnalyticsWindow;
  w.dataLayer ??= [];
  function gtag(..._args: unknown[]) {
    // biome-ignore lint/style/noArguments: Official Google command queue uses Arguments.
    const queued = arguments;
    w.dataLayer?.push(queued);
  }
  w.gtag ??= gtag;
  w.gtag(...args);
}
export function suspendAnalytics(id: string) {
  (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = true;
  activeId = "";
  lastPage = "";
}
export function configureAnalytics(
  id: string,
  accepted: boolean,
  options: { location?: string; consentKey?: string; surface?: "web" | "platform" } = {},
) {
  const allowed = accepted && readConsent(options.consentKey ?? CONSENT_KEY) === true;
  (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = !allowed;
  if (!allowed) {
    if (activeId) command("consent", "update", { analytics_storage: "denied" });
    activeId = "";
    lastPage = "";
    // Remove first-party GA cookies when permission is withdrawn.
    for (const cookie of document.cookie.split(";")) {
      const name = (cookie.split("=")[0] ?? "").trim();
      if (!/^_ga(?:_|$)/.test(name)) continue;
      for (const domain of ["", window.location.hostname, ".ando-ai.com"]) {
        document.cookie = `${name}=; Max-Age=0; Path=/;${domain ? ` Domain=${domain};` : ""} SameSite=Lax`;
      }
    }
    return;
  }
  const page = options.location ?? publicLocation(window.location.href);
  if (!/^G-[A-Z0-9]+$/.test(id) || !page) {
    suspendAnalytics(id);
    return;
  }
  if (activeId === id) return;
  activeConsentKey = options.consentKey ?? CONSENT_KEY;
  activeSurface = options.surface ?? "web";
  command("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  command("consent", "update", { analytics_storage: "granted" });
  command("js", new Date());
  command("config", id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: page,
    page_title: new URL(page).pathname,
    app_surface: activeSurface,
    page_referrer: safeReferrer(),
  });
  activeId = id;
  if (!document.getElementById("ando-ga4")) {
    const script = document.createElement("script");
    script.id = "ando-ga4";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(script);
  }
}
function safeReferrer(): string {
  try {
    return new URL(document.referrer).origin;
  } catch {
    return "";
  }
}
export function trackPage(location?: string) {
  if (!activeId || readConsent(activeConsentKey) !== true) return;
  const page = location ?? publicLocation(window.location.href);
  if (!page) {
    lastPage = "";
    return;
  }
  if (page === lastPage) return;
  // Keep automatic engagement events on the same sanitized page context.
  command("config", activeId, {
    send_page_view: false,
    page_location: page,
    page_title: new URL(page).pathname,
    page_referrer: lastPage || safeReferrer(),
    app_surface: activeSurface,
  });
  command("event", "page_view", {
    send_to: activeId,
    app_surface: activeSurface,
    page_location: page,
    page_title: new URL(page).pathname,
    page_referrer: lastPage || safeReferrer(),
  });
  lastPage = page;
}
export function trackMarketingEvent(
  name: "cta_click" | "download_click" | "generate_lead",
  parameters: Record<string, string> = {},
) {
  const page = publicLocation(window.location.href);
  if (!activeId || readConsent() !== true || !page) return;
  command("event", name, {
    ...parameters,
    send_to: activeId,
    app_surface: activeSurface,
    page_location: page,
    page_title: new URL(page).pathname,
    page_referrer: safeReferrer(),
  });
}
export function saveConsent(accepted: boolean, key = CONSENT_KEY) {
  try {
    failedStorageKeys.delete(key);
    localStorage.setItem(key, JSON.stringify({ accepted, at: Date.now() }));
  } catch {
    failedStorageKeys.add(key); // A failed write must never restore old acceptance.
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
}
